import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  BadgeCheck,
  ShoppingCart,
  ArrowUpRight,
  Info,
  Shirt,
  Mail,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { MembershipCallout } from "@/components/MembershipCallout";

const SITE_URL = "https://ogonotary.com";
const IDEA_LIST_URL = "https://amzn.to/4xjQ1ZX";
const FIELD_KIT_ARTICLE_PATH =
  "/blog/top-10-mobile-notary-field-kit-essentials";

// Definitive OGO TESTED product catalog.
// Every item on this page is personally owned and used by Dr. Gregory Byers.
// Products flagged as "OGO PICK" in the field-kit article are intentionally
// NOT included here — this page is the tested-and-used shortlist only.
const TESTED_PRODUCTS = [
  {
    slug: "vankean-notary-bag",
    title: "VANKEAN Mobile Notary Bag",
    subtitle: "Professional Mobile Notary Bag",
    review:
      "The center of the carry-in system. Dedicated compartments for laptop, documents, cables, power bank, pens, journal, and business essentials. The bag matters less than the system — this one gave me a repeatable place for everything.",
    url: "https://amzn.to/4cXFJGx",
    ctaLabel: "View the VANKEAN Bag on Amazon",
    testid: "gear-tested-vankean-bag",
  },
  {
    slug: "primary-scanner",
    title: "Primary Portable Document Scanner",
    subtitle: "Portable Document Scanner — Primary",
    review:
      "Duplex scanning, reliable document feeding, and a compact footprint. This is my go-to scanner for scanbacks and electronic delivery when assignments require it.",
    url: "https://amzn.to/4xQ8oWe",
    ctaLabel: "View the Primary Scanner on Amazon",
    testid: "gear-tested-scanner-primary",
  },
  {
    slug: "dual-tray-laser-printer",
    title: "Dual-Tray Laser Printer",
    subtitle: "Mobile Support Office Printer",
    review:
      "A dependable dual-tray laser is part of the mobile support office — one tray letter, one tray legal, no shuffling paper between refi and purchase packages. Understand the requirements of the companies you accept assignments from before investing.",
    url: "https://amzn.to/4xdhxYZ",
    ctaLabel: "View the Dual-Tray Laser Printer on Amazon",
    testid: "gear-tested-dual-tray-printer",
  },
  {
    slug: "professional-notary-journal",
    title: "Professional Notary Journal",
    subtitle: "Preparedness Record",
    review:
      "A well-designed journal gives you an organized record of your notarial activity. Journal requirements vary by jurisdiction — Florida's traditional notary law doesn't generally require one, but the Governor's manual recommends it as a protective record. If you use one, use it well.",
    url: "https://amzn.to/4qvZLO2",
    ctaLabel: "View the Notary Journal on Amazon",
    testid: "gear-tested-notary-journal",
  },
  {
    slug: "mobile-notary-trunk-organizer",
    title: "Mobile Notary Trunk Organizer",
    subtitle: "Vehicle Support Kit",
    review:
      "My favorite item on the list — it ties the whole system together. Designated places for backup supplies, paper, cables, power equipment, document cases, and safety items. The organizer itself matters less than the system it creates.",
    url: "https://amzn.to/4c5ygoy",
    ctaLabel: "View the Trunk Organizer on Amazon",
    testid: "gear-tested-trunk-organizer",
  },
];

export default function FieldTestedGearPage() {
  const [modalResource, setModalResource] = useState(null);

  const canonical = `${SITE_URL}/gear`;

  // ItemList schema so Google can see this as a curated product list.
  const listSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "OGO Notary Field-Tested Gear",
    description:
      "A definitive, growing list of mobile notary tools personally tested and used by Dr. Gregory Byers of OGO Notary & Signing Services.",
    url: canonical,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: TESTED_PRODUCTS.length,
    itemListElement: TESTED_PRODUCTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.title,
      url: p.url,
    })),
  };

  return (
    <>
      <Helmet>
        <title>
          Field-Tested Gear — Definitive OGO Tested Mobile Notary Kit | OGO Notary
        </title>
        <meta
          name="description"
          content="A definitive, growing list of the mobile notary tools Dr. Gregory Byers personally owns, uses, and tests in the field — VANKEAN bag, scanner, dual-tray laser printer, notary journal, trunk organizer, and more."
        />
        <meta
          name="keywords"
          content="OGO tested gear, field-tested notary gear, mobile notary equipment, notary bag, notary scanner, dual tray laser printer, notary journal, trunk organizer, Dr. Gregory Byers"
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Field-Tested Gear — Definitive OGO Tested Mobile Notary Kit"
        />
        <meta
          property="og:description"
          content="Growing catalog of the gear Dr. Gregory Byers personally owns, uses, and tests in the field."
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE_URL}/img/ogo-logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(listSchema)}
        </script>
      </Helmet>

      <PageHero
        eyebrow="Definitive · OGO TESTED · Amazon Associate"
        title="Field-Tested Gear."
        lead="A growing, definitive list of the mobile notary tools I personally own, use, and rely on in the field. Every product on this page carries the OGO TESTED mark — meaning it isn't research, it isn't opinion, it's what actually rides in my truck to signings."
      />

      {/* OGO TESTED banner — replaces old "coming soon" strip */}
      <section
        data-testid="gear-tested-banner"
        className="border-b border-white/10 bg-[#D4AF37]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-4 sm:py-5 flex items-center justify-center gap-3 text-center">
          <BadgeCheck
            className="w-4 h-4 sm:w-5 sm:h-5 text-[#142339] shrink-0"
            aria-hidden="true"
          />
          <p className="text-[#142339] text-xs sm:text-sm font-bold uppercase tracking-[0.18em]">
            Every product here is personally tested by Dr. Gregory Byers
          </p>
        </div>
      </section>

      {/* AFFILIATE DISCLOSURE */}
      <section
        data-testid="gear-affiliate-disclosure"
        className="border-b border-white/10 bg-[#0A1020]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-5 sm:py-6 flex items-start sm:items-center gap-3 sm:gap-4">
          <Info
            className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] shrink-0 mt-0.5 sm:mt-0"
            aria-hidden="true"
          />
          <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
            <span className="text-white font-semibold">
              #CommissionsEarned — Affiliate Disclosure:
            </span>{" "}
            As an Amazon Associate I earn from qualifying purchases. Every item
            below has been personally used in the field — recommendations are
            gear-first, commissions second.
          </p>
        </div>
      </section>

      {/* Companion article + Idea List row */}
      <section
        data-testid="gear-companion-row"
        className="border-b border-white/10 bg-[#0B1120]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-6 sm:py-8 grid gap-4 sm:grid-cols-2">
          <Link
            to={FIELD_KIT_ARTICLE_PATH}
            data-testid="gear-read-article-link"
            className="group flex items-center gap-4 border border-white/10 bg-[#142339] hover:border-[#D4AF37]/50 transition-colors p-5"
          >
            <div className="w-10 h-10 shrink-0 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:border-[#D4AF37]">
              <BookOpen className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">
                Read the Companion Article
              </div>
              <div className="text-white font-serif-display text-lg leading-tight">
                10 Practical Essentials for a Mobile Notary Field Kit
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#D4AF37] shrink-0" />
          </Link>
          <a
            href={IDEA_LIST_URL}
            target="_blank"
            rel="sponsored noopener noreferrer"
            data-testid="gear-idea-list-link"
            className="group flex items-center gap-4 border border-[#D4AF37]/60 bg-[#142339] hover:border-[#FDE047] transition-colors p-5"
          >
            <div className="w-10 h-10 shrink-0 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">
                Shop the Full Amazon Idea List
              </div>
              <div className="text-white font-serif-display text-lg leading-tight">
                OGO Notary Mobile Field Kit
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-[#D4AF37] group-hover:text-[#FDE047] shrink-0" />
          </a>
        </div>
      </section>

      {/* MEMBERSHIP CALLOUT */}
      <section className="py-10 sm:py-12 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <MembershipCallout variant="bar" testid="gear-membership-callout" />
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section
        data-testid="gear-tested-grid"
        className="py-20 sm:py-24 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <SectionEyebrow className="mb-4">
              The OGO Tested Catalog
            </SectionEyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
              Products I own, use, and trust in the field.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed text-base sm:text-lg">
              This list is intentionally short and honest. It grows as we test
              more equipment — never as a marketing exercise. If a product
              isn&apos;t on this page, we haven&apos;t personally tested it yet.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {TESTED_PRODUCTS.map((p, i) => (
              <TestedProductCard
                key={p.slug}
                product={p}
                position={i + 1}
                total={TESTED_PRODUCTS.length}
              />
            ))}
            <MoreOnTheWayCard />
          </div>
        </div>
      </section>

      {/* MERCH TEASER (unchanged) */}
      <section
        data-testid="gear-merch-teaser"
        className="py-20 sm:py-24 border-b border-white/10 bg-gradient-to-b from-[#0A1020] to-[#0B1120]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-14 h-14 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] mx-auto mb-6">
            <Shirt className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <SectionEyebrow className="mb-4">Merch — Coming Soon</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
            Official OGO Branded Apparel &amp; Gear
          </h2>
          <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Custom field hats, rugged mugs, and apparel —{" "}
            <span className="text-[#D4AF37]">Coming Soon.</span>
          </p>
          <button
            type="button"
            onClick={() =>
              setModalResource({
                slug: "ogo-branded-merch",
                title: "OGO Branded Apparel & Gear",
                source: "gear_page",
                comingSoon: true,
              })
            }
            data-testid="gear-merch-waitlist-cta"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            Notify Me When Live
          </button>
        </div>
      </section>

      <LeadCaptureModal
        resource={modalResource}
        onClose={() => setModalResource(null)}
      />
    </>
  );
}

function TestedProductCard({ product, position, total }) {
  const { title, subtitle, review, url, ctaLabel, testid } = product;
  return (
    <article
      data-testid={testid}
      className="group border border-white/10 bg-[#1A2B45] flex flex-col hover:border-[#D4AF37]/60 transition-colors"
    >
      <div className="p-6 sm:p-7 flex-1 flex flex-col">
        {/* Position + OGO TESTED chip */}
        <div className="flex items-center justify-between mb-5">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
            {String(position).padStart(2, "0")} · of {String(total).padStart(2, "0")}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] font-bold bg-[#D4AF37] text-[#142339] border border-[#D4AF37]">
            <BadgeCheck className="w-3 h-3" />
            OGO Tested
          </span>
        </div>

        <h3 className="font-serif-display text-xl sm:text-2xl text-white leading-snug">
          {title}
        </h3>
        {subtitle && (
          <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            {subtitle}
          </div>
        )}

        <p className="mt-5 text-slate-300 text-sm leading-relaxed flex-1">
          <span className="text-[#D4AF37] text-[10px] uppercase tracking-[0.22em] font-bold block mb-2">
            Field Review
          </span>
          {review}
        </p>

        <a
          href={url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          data-testid={`${testid}-cta`}
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          {ctaLabel}
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        <span className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
          #CommissionsEarned · Amazon Associate
        </span>
      </div>
    </article>
  );
}

function MoreOnTheWayCard() {
  return (
    <article
      data-testid="gear-tested-more-coming"
      className="relative border border-dashed border-[#D4AF37]/40 bg-[#1A2B45]/40 p-8 sm:p-10 flex flex-col justify-center text-center"
    >
      <div className="w-14 h-14 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] mx-auto mb-6">
        <Sparkles className="w-7 h-7" strokeWidth={1.5} />
      </div>
      <SectionEyebrow className="mb-4">The List is Growing</SectionEyebrow>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-white mb-4 leading-tight">
        More OGO Tested picks coming.
      </h3>
      <p className="text-slate-300 leading-relaxed text-sm max-w-md mx-auto">
        Every new addition earns its place through real use in the field. If a
        piece of gear helps me serve the next signer better, it makes the list.
      </p>
    </article>
  );
}
